import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    OnChanges,
    OnDestroy,
    Signal,
    signal,
    SimpleChanges,
    untracked,
    viewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoService } from '@jsverse/transloco';
import maplibregl, {
    ExpressionFilterSpecification,
    FilterSpecification,
    FitBoundsOptions,
    LngLat,
    LngLatBoundsLike,
    Map as MapLibreMap,
    MapGeoJSONFeature,
    MapGeoJSONFeature as MapLibreMapGeoJSONFeature,
    MapMouseEvent,
    Point,
    PointLike,
    RasterSourceSpecification,
    SourceSpecification,
} from 'maplibre-gl';
import { firstValueFrom } from 'rxjs';
import { v4 } from 'uuid';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { LabelBadgeComponent } from '@connect-services/client/components/label-badge/label-badge.component';
import { LegendButtonComponent } from '@connect-services/client/components/legend/legend-button/legend-button.component';
import { MapScaleControllerComponent } from '@connect-services/client/components/map/map-scale-controller/map-scale-controller.component';
import { MapExportModalComponent } from '@connect-services/client/components/map-export-modal/map-export-modal.component';
import { BasemapManagementModalComponent } from '@connect-services/client/components/modals/basemap-management-modal/basemap-management-modal.component';
import {
    ManagePinModalComponent,
    type ManagePinModalData,
} from '@connect-services/client/components/modals/manage-pin-modal/manage-pin-modal.component';
import { MapModalComponent } from '@connect-services/client/components/modals/map-modal/map-modal.component';
import { PinPanelComponent } from '@connect-services/client/components/pin-panel/pin-panel.component';
import { NavigationStateService } from '@connect-services/client/guards/reload.guard';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
    CenterEntityBroadcastChannelData,
    HighlightEntityBroadcastChannelData,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { GeomLayer } from '@connect-services/client/models/geom-layer.model';
import {
    ActionsEnum,
    EntityEnum,
    FieldInterventionEnum,
    PanelActions,
} from '@connect-services/client/models/panel.model';
import { RouteNameEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import {
    BasemapInfo,
    BasemapLayerType,
    BASEMAPS_CATALOG,
    BasemapService,
    TileSourceTypeEnum,
} from '@connect-services/client/services/basemap.service';
import { BroadcastChannelService, SynopticData } from '@connect-services/client/services/broadcast-channel.service';
import { CreateFieldInterventionService } from '@connect-services/client/services/create-field-intervention.service';
import { DataExportService } from '@connect-services/client/services/data-export.service';
import { DepositService } from '@connect-services/client/services/deposit.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import { FeatureService } from '@connect-services/client/services/feature.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { GeohashService } from '@connect-services/client/services/geohash.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { GoogleStreetViewService } from '@connect-services/client/services/immersive-view/google-street-view.service';
import {
    IMMERSIVE_VIEW_MARKER_HTML,
    ImmersiveViewPosition,
    ImmersiveViewService,
} from '@connect-services/client/services/immersive-view/immersive-view.service';
import { MapGeohashService } from '@connect-services/client/services/map/geohash/map-geohash.service';
import { MapInteractionService } from '@connect-services/client/services/map/interaction/map-interaction.service';
import { MapTooltipService } from '@connect-services/client/services/map/map-tooltip.service';
import { MapMaskService } from '@connect-services/client/services/map/mask/map-mask.service';
import { MapModeService } from '@connect-services/client/services/map/mode/map-mode.service';
import {
    MapModalSelectionModeEnum,
    SelectionService,
} from '@connect-services/client/services/map/selection/selection.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapClickIndicatorService } from '@connect-services/client/services/map-click-indicator.service';
import { MapControlService } from '@connect-services/client/services/map-control.service';
import {
    ActiveGeometryTypeEnum,
    MapCreationGeomService,
} from '@connect-services/client/services/map-creation-geom.service';
import {
    MapDrawService,
    PendingCenterEntityRequest,
    PendingEntityMapRequestBase,
} from '@connect-services/client/services/map-draw.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import {
    EditModeCapabilities,
    EditModeType,
    MapEditModeService,
} from '@connect-services/client/services/map-edit-mode.service';
import { MapFilterService } from '@connect-services/client/services/map-filter.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapLayerStorageService } from '@connect-services/client/services/map-layer-storage.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapGeoJSONFeatureWithLayer, MapPopupService } from '@connect-services/client/services/map-popup.service';
import { MapSearchService } from '@connect-services/client/services/map-search.service';
import { DrawSourcesOptions, MapSourceService } from '@connect-services/client/services/map-source.service';
import { MeasurementToolService } from '@connect-services/client/services/measurement-tool.service';
import { NetworkPathMapService } from '@connect-services/client/services/network-path/network-path-map.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { PinService } from '@connect-services/client/services/pin.service';
import { RouterService } from '@connect-services/client/services/router.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import { environment } from '@connect-services/src/environments/environment';
import {
    DataEntity,
    FieldIntervention,
    FieldInterventionEntitiesService,
    Geometry,
    Layer,
    LayerFilterResult,
    MapboxStyle,
    PermissionAliasEnum,
    Project,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

export enum MapSourceTypeEnum {
    FIELD_INTERVENTION = 'FIELD_INTERVENTION',
    GEOM = 'GEOM',
    LAYER = 'LAYER',
    URL = 'URL',
}

export class MapBaseSource<T> {
    public constructor(
        public sourceId: string,
        public style: MapboxStyle[],
        public name: string,
        public type: MapSourceTypeEnum,
        public data: T,
        public isVisible: boolean,
        public minzoom?: number | null,
        public maxzoom?: number | null,
    ) {}
}

export class MapFieldInterventionSource extends MapBaseSource<FieldIntervention> {
    public layerFilter?: string;

    public constructor(
        sourceId: string,
        fieldIntervention: FieldIntervention,
        style: MapboxStyleWithZoom,
        layerFilter?: string,
    ) {
        super(
            sourceId,
            style.style,
            fieldIntervention.name,
            MapSourceTypeEnum.FIELD_INTERVENTION,
            fieldIntervention,
            false,
            style.minzoom,
            style.maxzoom,
        );
        this.layerFilter = layerFilter;
    }
}

export class MapGeomSource extends MapBaseSource<Geometry> {
    public constructor(sourceId: string, style: MapboxStyle[], geometry: Geometry) {
        super(sourceId, style, sourceId, MapSourceTypeEnum.GEOM, geometry, true);
    }
}

export class MapboxStyleWithZoom {
    public constructor(
        public id: string,
        public style: MapboxStyle[],
        public minzoom?: number | null,
        public maxzoom?: number | null,
    ) {}
}

export class MapLayerSource extends MapBaseSource<GeomLayer> {
    public depositName?: string;
    public isActivated?: boolean;
    public isFiltered?: boolean;
    public isInterstandard?: boolean;
    public layerTopic?: string | null;

    public constructor(layer: GeomLayer, style?: MapboxStyleWithZoom, isActivated?: boolean, depositName?: string) {
        super(
            layer.id,
            style?.style ?? [],
            layer.name,
            MapSourceTypeEnum.LAYER,
            layer,
            layer.isVisible,
            style?.minzoom,
            style?.maxzoom,
        );
        this.depositName = depositName;
        this.isActivated = isActivated;
        this.layerTopic = layer.layerTopic ?? null;
        this.isInterstandard = layer.type === Layer.TypeEnum.INTERSTANDARD;
    }
}

export type ViewParam = {
    color?: string;
    icon: IconDefinition;
    labelTranslation: string;
};

const defaultRasterSourceSpecification: RasterSourceSpecification = {
    maxzoom: 18,
    tileSize: 512,
    type: 'raster',
};

interface SelectedEntity extends DataEntity {
    identifier: string;
    layerId: string;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LabelBadgeComponent,
        LegendButtonComponent,
        MapModalComponent,
        MapScaleControllerComponent,
        PinPanelComponent,
        RessourcesModule,
    ],
    selector: 'app-map',
    templateUrl: './map.component.html',
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
    public static readonly backgroundSource = signal<TileSourceTypeEnum>(TileSourceTypeEnum.OSM);
    public static readonly fitBoundsOptions: FitBoundsOptions = {
        duration: 2000,
        essential: true,
        linear: false,
        maxZoom: 14,
        padding: 40,
    };
    public static readonly sourceSpecifications: Record<string, SourceSpecification> = Object.values(
        BASEMAPS_CATALOG,
    ).reduce<Record<string, SourceSpecification>>((acc, base: BasemapInfo) => {
        if (!base.sourceId || !base.url || base.layerType !== BasemapLayerType.RASTER) {
            return acc;
        }
        acc[base.sourceId] = {
            ...defaultRasterSourceSpecification,
            attribution: base.attribution,
            tiles: [base.url],
        };

        return acc;
    }, {});

    private static readonly defaultCenter = new LngLat(2.6, 47); // Center of France coordinates

    public readonly canBasemap = input<boolean>(false);
    public readonly canClickFeature = input<boolean>(true);
    public readonly canDraw = input<boolean>(false);
    public readonly canMapExport = input<boolean>(false);
    public readonly canUseGeographicFilter = input<boolean>(false);
    public readonly center = input<LngLat | undefined>();
    public readonly currentUrl: Signal<string[] | undefined> = signal<string[] | undefined>(undefined);
    public readonly defaultProjection = input<Project.ProjectionEnum>(Project.ProjectionEnum._2154);
    public readonly editModeCapabilities = input.required<EditModeCapabilities>();
    public environment = environment;
    public readonly isMapEditToolbarActive = computed(() => {
        return this.editModeCapabilities().enabled && this.mapEditModeService.isMapEditModeActive();
    });
    public readonly isSearchedFeatureActive = signal<boolean>(false);
    public map?: MapLibreMap;
    public readonly PermissionAliasEnum = PermissionAliasEnum;
    public readonly sources = input<MapBaseSource<unknown>[]>([]);

    protected readonly backgroundSource = MapComponent.backgroundSource;
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected readonly hasProjects = computed(
        () => !!this.activatedRoute.snapshot.paramMap.get(RoutePathEnum.PROJECT_ID),
    );
    protected readonly isMapOnEdit = computed(
        () => this.mapEditModeService.isAddLineVertexActive() || !!this.mapEditModeService.activeGeometryType(),
    );
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly mapModalService = inject(MapModalService);
    protected readonly mapModeService = inject(MapModeService);
    protected readonly mapTooltipService = inject(MapTooltipService);
    protected readonly selectionService = inject(SelectionService);

    private readonly activatedRoute = inject(ActivatedRoute);
    private addressMarker: maplibregl.Marker | null = null;
    private baseMapDialogRef: MatDialogRef<BasemapManagementModalComponent> | null = null;
    private readonly basemapService = inject(BasemapService);
    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);
    private readonly broadcastChannelService = inject(BroadcastChannelService);
    private readonly createFieldInterventionService = inject(CreateFieldInterventionService);
    private readonly dataExportService = inject(DataExportService);
    private readonly depositService = inject(DepositService);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly featureService = inject(FeatureService);
    private fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly geohashService = inject(GeohashService);
    private readonly googleStreetViewService = inject(GoogleStreetViewService);
    private readonly heightMapForVerticalButtons = 400;
    private readonly highlightBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.HIGHLIGHT);
    private readonly homeService = inject(HomeService);
    private immersiveViewMarker: maplibregl.Marker | null = null;
    private readonly immersiveViewService = inject(ImmersiveViewService);
    private isDestroyed = false;
    private readonly isEditActionActive = signal<boolean>(false);
    private readonly isFieldInterventionPanelActive = computed<boolean>(() => {
        const currentPanel: PanelActions | null = this.panelService.currentPanel();

        return (
            currentPanel === ActionsEnum.FIELD_INTERVENTION ||
            currentPanel === FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD
        );
    });
    private readonly isMapClickDisabled = computed<boolean>(() => {
        const currentPanel: PanelActions | null = this.panelService.currentPanel();

        const disabledPanels: readonly PanelActions[] = [
            FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD,
            EntityEnum.ENTITIES_LIST,
        ];

        return (
            !this.map ||
            this.dataExportService.isActive() ||
            this.geographicFilterService.isSelectZoneActive() ||
            this.mapDrawService.isPolygonDrawSessionActive() ||
            this.selectionService.isFieldInterventionAreaMode() ||
            disabledPanels.some((panel: PanelActions) => panel === currentPanel)
        );
    });
    private isPendingEditEntityGeometryProcessing = false;
    private lastEditCapabilitiesEnabled: boolean | null = null;
    private readonly layerBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private layersCompletelyFilteredWithOriginalVisibility: Map<string, boolean> = new Map<string, boolean>();
    private readonly mapClickIndicatorService = inject(MapClickIndicatorService);
    private readonly mapContainer = viewChild.required<ElementRef<HTMLElement>>('map');
    private readonly mapControlService = inject(MapControlService);
    private readonly mapCreationGeomService = inject(MapCreationGeomService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly mapFilterService = inject(MapFilterService);
    private readonly mapGeohashService = inject(MapGeohashService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapInteractionService = inject(MapInteractionService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapLayerStorageService = inject(MapLayerStorageService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly mapScaleControllerComponent = viewChild(MapScaleControllerComponent);
    private readonly mapSearchService = inject(MapSearchService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private readonly measurementToolService = inject(MeasurementToolService);
    private readonly networkPathMapService = inject(NetworkPathMapService);
    private readonly panelService = inject(PanelService);
    private readonly pinService = inject(PinService);
    private readonly projectBloc = inject(ProjectBloc);
    private resizeObserver?: ResizeObserver;
    private readonly router = inject(Router);
    private readonly routerService = inject(RouterService);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly sourcesServices = inject(SourcesService);
    private readonly translocoService = inject(TranslocoService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor(private readonly injector: Injector) {
        effect(() => {
            if (this.pinService.isActive()) {
                untracked(() => this.closeBasemapModal());
            }
        });

        effect(async () => {
            await this.handleMapSearch();
        });
        effect(() => {
            this.handleAddressMarker();
        });
        effect(() => {
            this.immersiveViewService.markerPosition();

            this.handleImmersiveViewMarker();
        });
        effect(() => {
            this.applyFilters();
        });
        effect(async () => {
            if (!this.mapEditModeService.exitEditModeSignal() || !this.map) {
                return;
            }

            this.mapLayerStorageService.resetLayerGeohash([
                ...this.mapEditGeomService.editedLayerId(),
                ...this.mapCreationGeomService.editedLayerIds(),
            ]);
            await this.redrawAfterExitEditMode();
        });
        effect(() => {
            if (!this.selectionService.isSelectionActive()) {
                return;
            }
            this.mapModalService.open(MapModal.SELECTION);

            if (this.selectionService.isFieldInterventionAreaMode()) {
                return;
            }

            this.selectionService.setModalMode(MapModalSelectionModeEnum.SELECTION);
        });

        effect(() => {
            const request = this.mapDrawService.pendingCenterEntityRequest();
            if (!request || !MapBaseService.isMapReady() || !this.mapDrawService.isMapPresent()) {
                return;
            }

            void this.centerPendingEntity(request);
        });

        effect(() => {
            const request = this.mapDrawService.pendingEditEntityGeometryRequest();

            if (!request || !MapBaseService.isMapReady() || !this.mapDrawService.isMapPresent()) {
                return;
            }

            void this.editPendingEntityGeometry(request);
        });

        this.currentUrl = this.routerService.getUrl();

        this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastChannelEvent<null | SynopticData>>): void => {
            const payload = event.data;
            if (payload?.isOpening && payload.source === BroadcastChannelSourceEnum.SYNOPTIC_PLAN) {
                this.broadcastChannelService.openSelectedSynopticOnNewWindow(this.broadcastChannel);
                this.broadcastChannelService.externalOpenInProgress.set(false);
            }
        };

        this.layerBroadcastChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<CenterEntityBroadcastChannelData>>,
        ): void => {
            this.handleCenterEntityBroadcastChannelEvent(event);
        };

        this.highlightBroadcastChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<HighlightEntityBroadcastChannelData>>,
        ): void => {
            this.handleHighlightEntityBroadcastChannelEvent(event);
        };

        MapSourceService.isMapOrProjectFirstLoading.set(true);
    }

    public async ngAfterViewInit(): Promise<void> {
        await this.googleStreetViewService.initGoogleMapsApi();

        this.map = new MapLibreMap({
            attributionControl: false,
            center: this.center() ?? MapComponent.defaultCenter,
            container: this.mapContainer().nativeElement,
            style: {
                glyphs: `${window.location.origin}/assets/fonts/{fontstack}/{range}.pbf`,
                layers: [
                    {
                        id: MapComponent.backgroundSource(),
                        source: MapComponent.backgroundSource(), // This must match the source key
                        type: 'raster',
                    },
                ],
                sources: MapComponent.sourceSpecifications,
                sprite: [
                    {
                        id: 'default',
                        url: `${window.location.origin}/assets/sprites/CS_default_sprite`,
                    },
                    {
                        id: 'star-eau',
                        url: `${window.location.origin}/assets/sprites/CS_star-eau_sprite`,
                    },
                ],
                version: 8,
            },
        });

        this.mapSourceService.bindMap(this.map);
        this.basemapService.bindMap(this.map);
        this.mapControlService.bindMap(this.map);
        this.pinService.bindMap(this.map);
        this.mapMaskService.bindMap(this.map);
        await this.mapEditModeService.bindMap(this.map);

        this.mapControlService.addControls({
            basemap: {
                enabled: this.canBasemap(),
                listener: () => {
                    this.openBasemapModal();
                },
            },
            draw: this.canDraw(),
            editMode: {
                enabled: this.editModeCapabilities().enabled,
                listener: () => {
                    this.toggleEditMode();
                },
            },
            geolocate: true,
            immersiveView: {
                enabled: this.immersiveViewService.canUseImmersiveView(),
            },
            mapExport: {
                enabled: this.canMapExport(),
                listener: () => {
                    this.openMapExportModal();
                },
            },
            measurement: {
                enabled: true,
                listener: (isActive: boolean) => {
                    if (!isActive) {
                        this.measurementToolService.disableMeasurementTool();

                        return;
                    }

                    this.measurementToolService.enableMeasurementTool();
                },
            },
            navigation: {
                enabled: true,
            },
            pins: {
                enabled: true,
            },
            selectionModal: {
                enabled: this.canUseGeographicFilter(),
            },
        });

        this.initializeMapEvents();
        this.mapScaleControllerComponent()!.setMap(this.map);

        const ctrlContainer = document.querySelector('.maplibregl-ctrl-bottom-right') as HTMLElement;
        const mapContainer = this.mapContainer().nativeElement;

        this.resizeObserver = new ResizeObserver(() => {
            if (this.isDestroyed) {
                return;
            }

            const mapHeight = mapContainer?.offsetHeight ?? 0;

            if (mapHeight <= this.heightMapForVerticalButtons) {
                ctrlContainer?.classList.add('map-controls-inline');
            } else {
                ctrlContainer?.classList.remove('map-controls-inline');
            }
        });

        if (mapContainer && ctrlContainer) {
            this.resizeObserver.observe(mapContainer);
        }
    }

    public async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (!this.map) {
            return;
        }

        if (changes['center']?.currentValue && !this.mapGeohashService.centralGeohash) {
            this.mapGeohashService.updateCentralGeohash(this.map);
        }

        if (changes['sources'] && !changes['sources'].currentValue.length) {
            this.mapSourceService.resetMapBounds();
        }

        if (changes['center'] && changes['center'].currentValue !== changes['center'].previousValue) {
            this.mapSourceService.clearSources(MapComponent.backgroundSource(), this.getSourcesToKeep());
            this.map.jumpTo({ center: changes['center'].currentValue, zoom: MapBaseService.defaultZoom });
        }

        if (changes['sources'] && changes['sources'].currentValue !== changes['sources'].previousValue) {
            this.mapSourceService.clearSources(MapComponent.backgroundSource(), this.getSourcesToKeep());
            await this.drawSources(changes['sources'].currentValue);

            this.applyFilters();

            this.measurementToolService.disableMeasurementTool();

            const measurementToolControl = document.getElementById('measurement-tool-control') as HTMLButtonElement;
            if (measurementToolControl) {
                measurementToolControl.disabled = false;
            }
        }

        if (changes['canMapExport']) {
            this.mapControlService.loadMapExportControl({
                enabled: this.canMapExport(),
                listener: () => {
                    this.openMapExportModal();
                },
            });
        }

        if (changes['canUseGeographicFilter']) {
            this.mapControlService.reloadSelectionModalControl({ enabled: this.canUseGeographicFilter() });
        }

        if (changes['editModeCapabilities']) {
            if (this.lastEditCapabilitiesEnabled === this.editModeCapabilities().enabled) {
                return;
            }

            this.lastEditCapabilitiesEnabled = this.editModeCapabilities().enabled;
            this.mapControlService.loadMapEditModeControl(
                {
                    enabled: this.editModeCapabilities().enabled,
                    listener: () => {
                        this.toggleEditMode();
                    },
                },
                true,
            );

            if (this.editModeCapabilities().enabled || !this.mapEditModeService.isMapEditModeActive()) {
                return;
            }

            await this.mapEditModeService.cleanupMapUiForEditMode();
        }
    }

    public ngOnDestroy(): void {
        this.isDestroyed = true;
        this.resizeObserver?.disconnect();
        this.mapDrawService.clearEventListeners();

        this.closeBasemapModal();

        if (this.mapEditModeService.isAddEntityModeActive() || this.mapEditModeService.isGeographicEditModeActive()) {
            this.mapEditModeService.toggleLayerEditMode([], EditModeType.NONE);
        }

        MapBaseService.isMapReady.set(false);
        this.pinService.close();

        try {
            this.map?.remove();
        } catch {
            /* empty */
        }
    }

    protected leaveCreationMode(): void {
        this.mapEditModeService.toggleLayerEditMode([], EditModeType.NONE);

        this.mapEditModeService.activeGeometryType.set(null);
    }

    private applyFilterOnLayersByEntityIds(layerId: string): void {
        const layerFilter: LayerFilterResult = this.mapFilterService
            .appliedFiltersResult()
            .find((result: LayerFilterResult) => layerId.startsWith(result.layerId))!;

        const baseFilter: FilterSpecification | undefined = this.mapFilterService.initialFilterByLayer.has(layerId)
            ? this.mapFilterService.initialFilterByLayer.get(layerId)
            : (this.map?.getFilter(layerId) ?? undefined);

        if (!this.mapFilterService.initialFilterByLayer.has(layerId)) {
            this.mapFilterService.initialFilterByLayer.set(
                layerId,
                baseFilter ? structuredClone(baseFilter) : undefined,
            );
        }

        const entityIds: ExpressionFilterSpecification = ['in', ['get', 'uuid'], ['literal', layerFilter.entityIds]];

        if (!baseFilter) {
            const newFilter: FilterSpecification = ['all', entityIds];
            this.map?.setFilter(layerId, newFilter);

            return;
        }

        const newFilter: ExpressionFilterSpecification = [
            'all',
            baseFilter,
            entityIds,
        ] as ExpressionFilterSpecification;

        this.map?.setFilter(layerId, newFilter);
    }

    private async applyFilters(): Promise<void> {
        if (!this.mapFilterService.selectedFilters().length && !this.mapFilterService.previewResultFilters()) {
            this.clearFilters();

            return;
        }

        const filteredLayerIds = await this.applyFiltersOnWholeLayer();

        this.applyFiltersByAttributesValues(filteredLayerIds);
    }

    private applyFiltersByAttributesValues(wholeFilteredLayerIds: string[]): void {
        this.map
            ?.getLayersOrder()
            .filter((layerId: string) =>
                wholeFilteredLayerIds.some((filteredLayerId: string) => layerId.startsWith(filteredLayerId)),
            )
            .forEach((layerId: string) => {
                this.applyFilterOnLayersByEntityIds(layerId);
            });

        Array.from(this.mapFilterService.initialFilterByLayer.entries())
            .filter(
                ([layerId]: [string, FilterSpecification | undefined]) =>
                    !wholeFilteredLayerIds.some((filteredLayerId: string) => layerId.startsWith(filteredLayerId)),
            )
            .forEach(([layerId, filter]: [string, FilterSpecification | undefined]) => {
                this.map?.setFilter(layerId, filter);
            });
    }

    private async applyFiltersOnWholeLayer(): Promise<string[]> {
        await this.mapFilterService.updateAppliedFilters();

        const filteredLayerIds: string[] = this.mapFilterService
            .appliedFiltersResult()
            .map((result: LayerFilterResult) => result.layerId);

        this.layersCompletelyFilteredWithOriginalVisibility.forEach((originalVisibility: boolean, layerId: string) => {
            if (
                this.mapFilterService
                    .appliedFiltersResult()
                    .some(
                        (filterResult: LayerFilterResult) => filterResult.filtered && filterResult.layerId === layerId,
                    )
            ) {
                return;
            }

            const mapSource = this.sourcesServices
                .sources()
                .find((source: MapBaseSource<unknown>) => source.sourceId === layerId);

            if (!(mapSource instanceof MapLayerSource)) {
                return;
            }

            if (!this.mapSourceService.isSourceVisible(mapSource) && originalVisibility) {
                this.mapSourceService.toggleSourceVisibility(mapSource);
            }

            this.sourcesServices.sources.update((sources: MapBaseSource<unknown>[]) =>
                sources.map((source: MapBaseSource<unknown>) => {
                    if (source.sourceId === layerId && source instanceof MapLayerSource) {
                        source.isFiltered = false;
                    }

                    return source;
                }),
            );
            this.layersCompletelyFilteredWithOriginalVisibility.delete(layerId);
        });

        this.mapFilterService
            .appliedFiltersResult()
            .filter(
                (filter: LayerFilterResult) =>
                    filter.filtered && !this.layersCompletelyFilteredWithOriginalVisibility.has(filter.layerId),
            )
            .forEach((filter: LayerFilterResult) => {
                const mapSource = this.sourcesServices
                    .sources()
                    .find((source: MapBaseSource<unknown>) => source.sourceId === filter.layerId);

                if (!(mapSource instanceof MapLayerSource)) {
                    return;
                }

                this.layersCompletelyFilteredWithOriginalVisibility.set(filter.layerId, mapSource.isVisible);

                if (this.mapSourceService.isSourceVisible(mapSource) && mapSource.isActivated) {
                    this.mapSourceService.toggleSourceVisibility(mapSource);
                }

                this.sourcesServices.sources.update((sources: MapBaseSource<unknown>[]) =>
                    sources.map((source: MapBaseSource<unknown>) => {
                        if (source.sourceId === filter.layerId && source instanceof MapLayerSource) {
                            source.isFiltered = true;
                        }

                        return source;
                    }),
                );
            });

        return filteredLayerIds;
    }

    private async centerPendingEntity(request: PendingCenterEntityRequest): Promise<void> {
        if (!this.map) {
            return;
        }

        this.mapDrawService.clearPendingCenterEntityRequest();

        await this.waitForMapIdleOnce();

        if (!this.map) {
            return;
        }

        this.mapDrawService.centerMapOnEntityAndZoomIn(request.layerId, request.geomBboxEntity, request.geom);

        const renderedFeature = await this.waitForRenderedEntityFeature(request.entityId);
        if (!renderedFeature) {
            return;
        }

        const { source } = this.mapDrawService.getSourceAndEntityType(request.layerId, request.geom);
        if (!source) {
            return;
        }

        this.mapHighlightEntityService.manageHighlightFeature(source, [request.entityId], request.entityType);
    }

    private clearFilters(): void {
        Array.from(this.mapFilterService.initialFilterByLayer.entries()).forEach(
            ([layerId, filter]: [string, FilterSpecification | undefined]) => {
                if (!this.mapLayerService.layerExists(layerId)) {
                    this.mapFilterService.initialFilterByLayer.delete(layerId);

                    return;
                }
                this.map?.setFilter(layerId, filter);
            },
        );
        this.layersCompletelyFilteredWithOriginalVisibility.forEach((originalVisibility: boolean, layerId: string) => {
            const mapSource = this.sourcesServices
                .sources()
                .find((source: MapBaseSource<unknown>) => source.sourceId === layerId);

            if (!(mapSource instanceof MapLayerSource)) {
                return;
            }

            mapSource.isFiltered = false;

            if (!mapSource.isVisible && originalVisibility) {
                if (this.mapLayerService.isSourceVisible(mapSource)) {
                    mapSource.isVisible = true;
                }

                this.mapLayerService.toggleSourceVisibility(mapSource);
            }
        });
        this.layersCompletelyFilteredWithOriginalVisibility.clear();

        this.mapFilterService.clearFilters();
    }

    private clearPendingEditEntityGeometryProcessing(): void {
        this.mapDrawService.clearPendingEditEntityGeometryRequest();
        this.isPendingEditEntityGeometryProcessing = false;
    }

    private closeBasemapModal(): void {
        const dialogRef = this.baseMapDialogRef;
        this.baseMapDialogRef = null;
        this.basemapService.isActive.set(false);
        dialogRef?.close();
    }

    private createRenderedEntityFeatureListener(
        entityId: string,
        startedAt: number,
        timeoutInMilliseconds: number,
        resolve: (feature: MapGeoJSONFeature | undefined) => void,
    ): () => void {
        const listener = (): void => {
            const feature = this.getRenderedEntityFeature(entityId);
            if (feature) {
                this.map?.off('render', listener);
                resolve(feature);

                return;
            }

            if (Date.now() - startedAt >= timeoutInMilliseconds) {
                this.map?.off('render', listener);
                resolve(undefined);
            }
        };

        return listener;
    }

    private async drawSources(
        sources: MapBaseSource<unknown>[],
        fitBounds: boolean = true,
        withGeohash: boolean = true,
    ): Promise<void> {
        if (!sources.length) {
            return;
        }

        const options: DrawSourcesOptions = {
            fitBounds: fitBounds,
        };

        if (withGeohash && this.mapGeohashService.centralGeohash) {
            options.geohashes = [
                ...this.geohashService.findNeighbors(
                    this.mapGeohashService.centralGeohash,
                    MapGeohashService.geohashNeighborsMatrixSize,
                ),
                this.mapGeohashService.centralGeohash,
            ];
        }

        const isEditModeActive = this.mapEditModeService.isMapEditModeActive();

        await this.mapSourceService.drawSources(sources, options, isEditModeActive);
    }

    private async editPendingEntityGeometry(request: PendingEntityMapRequestBase): Promise<void> {
        if (!this.map || this.isPendingEditEntityGeometryProcessing) {
            return;
        }

        this.isPendingEditEntityGeometryProcessing = true;

        try {
            await this.waitForMapIdleOnce();

            if (!this.map) {
                return;
            }

            this.mapDrawService.centerMapOnEntityAndZoomIn(request.layerId, request.geomBboxEntity, request.geom);

            const renderedFeature = await this.waitForRenderedEntityFeature(request.entityId);
            if (!renderedFeature) {
                return;
            }

            this.mapEditModeService.toggleLayerEditMode([request.layerId], EditModeType.GEOGRAPHIC, request.entityId);
        } finally {
            this.clearPendingEditEntityGeometryProcessing();
        }
    }

    private filterClickedFeatures(features: MapGeoJSONFeature[], sourceIds?: string[]): MapGeoJSONFeatureWithLayer[] {
        if (this.measurementToolService.isActive()) {
            return [];
        }
        features = this.mapClickIndicatorService.filterIndicatorSource(features);

        const availableSources: MapBaseSource<unknown>[] = this.resolveAvailableClickSources();

        return features.reduce(
            (acc: MapGeoJSONFeatureWithLayer[], feature: MapLibreMapGeoJSONFeature): MapGeoJSONFeatureWithLayer[] => {
                const source: MapBaseSource<unknown> = availableSources.find(
                    (source: MapBaseSource<unknown>) => source.sourceId === feature.source,
                )!;

                if (!source || (sourceIds?.length && !sourceIds.includes(source.sourceId))) {
                    return acc;
                }

                if (
                    source instanceof MapGeomSource ||
                    (source instanceof MapLayerSource &&
                        (source.data.type === Layer.TypeEnum.WMS || source.data.type === Layer.TypeEnum.GEOM))
                ) {
                    return acc;
                }

                if (source instanceof MapFieldInterventionSource) {
                    const dataAttributes = JSON.parse(feature.properties['dataAttributes'] || {});

                    return [
                        ...acc,
                        Object.assign({}, feature, {
                            geometry: feature.geometry,
                            layer: {
                                ...feature.layer,
                                name: source.name,
                            },
                            properties: {
                                uuid: dataAttributes.businessIdentifier,
                                ...feature.properties,
                                ...dataAttributes,
                            },
                        }),
                    ];
                }

                return [
                    ...acc,
                    Object.assign({}, feature, {
                        geometry: feature.geometry,
                        layer: {
                            ...feature.layer,
                            name: source.name,
                        },
                    }),
                ];
            },
            [],
        );
    }

    private getFeatureInCoordinates(point: Point, sourceIds?: string[]): MapGeoJSONFeatureWithLayer[] {
        const bbox: [PointLike, PointLike] = [
            [point?.x - MapSourceService.defaultZoomSensibility, point?.y - MapSourceService.defaultZoomSensibility],
            [point?.x + MapSourceService.defaultZoomSensibility, point?.y + MapSourceService.defaultZoomSensibility],
        ];

        const features: MapGeoJSONFeatureWithLayer[] = this.filterClickedFeatures(
            this.map!.queryRenderedFeatures(bbox),
            sourceIds,
        );
        features.forEach((feature: MapGeoJSONFeatureWithLayer) => {
            if (!('uuid' in feature.properties)) {
                feature.properties = { ...feature.properties, uuid: v4() };
            }
        });

        const uniqueFeatureMap = new Map<string, MapGeoJSONFeatureWithLayer>(
            features.map((feature: MapGeoJSONFeatureWithLayer) => [
                `${feature.properties['uuid']}_${feature.source}`,
                feature,
            ]),
        );

        const uniqueFeatures = Array.from(uniqueFeatureMap.values());

        if (!this.isFieldInterventionPanelActive()) {
            return uniqueFeatures;
        }

        if (uniqueFeatureMap.size > 1) {
            this.mapClickIndicatorService.createIndicatorSourceOnClick(point, bbox);
        } else {
            this.mapClickIndicatorService.removeIndicatorSource();
        }

        return uniqueFeatures;
    }

    private getRenderedEntityFeature(entityId: string): MapGeoJSONFeature | undefined {
        return this.map
            ?.queryRenderedFeatures()
            .find((item: MapGeoJSONFeature) => item.properties?.['uuid'] === entityId);
    }

    private async getSearchedFeature(selectedEntity: SelectedEntity, currentSourceId: string): Promise<void> {
        if (!selectedEntity.geom) {
            return;
        }

        const feature: MapGeoJSONFeatureWithLayer | undefined = this.mapSourceService.getRenderedEntityFeature(
            currentSourceId,
            selectedEntity.uuid,
            selectedEntity.geom,
        );

        if (!feature) {
            return;
        }

        await this.navigateToEntityDetail(feature);

        this.isSearchedFeatureActive.set(false);
    }

    private getSourcesToKeep(): MapBaseSource<unknown>[] {
        if (this.createFieldInterventionService.isCreateFieldIntervention()) {
            return [...(this.sources() ?? []), ...this.createFieldInterventionService.currentSources()];
        }

        if (this.entitiesTableByLayerService.isPlanningSelectionActive()) {
            return [...(this.sources() ?? []), ...this.entitiesTableByLayerService.currentSources()];
        }

        return this.sources() ?? [];
    }

    private handleAddressMarker(): void {
        this.removeAddressMarker();

        const addressItem = this.mapSearchService.selectedAddressItem();
        if (!addressItem) {
            return;
        }

        const el = document.createElement('div');
        el.innerHTML = `<img src="assets/images/icons/location-marker.svg" style="width: 30px; height: 30px;" id="location-marker" />`;

        const coordinates = addressItem.geometry.coordinates;

        this.addressMarker = new maplibregl.Marker({
            anchor: 'bottom',
            element: el,
        })
            .setLngLat([coordinates[0], coordinates[1]])
            .addTo(this.map!);

        this.map!.flyTo({
            center: [coordinates[0], coordinates[1]],
            essential: true,
            speed: 2.0,
            zoom: 18,
        });
    }

    private handleCenterEntityBroadcastChannelEvent(
        event: MessageEvent<BroadcastChannelEvent<CenterEntityBroadcastChannelData>>,
    ): void {
        const data = event.data;
        if (!data.additionalData) {
            return;
        }

        const { entityId, entityType, geomBoxEntity, source } = data.additionalData;

        if (!source) {
            return;
        }

        const defaultZoom = this.mapDrawService.getSourceVisibleZoom(source);
        this.mapDrawService.centerMapOnSpecificEntity(geomBoxEntity, defaultZoom);
        this.map?.once('idle', () => {
            if (!this.selectionStateService.isEditingSelection()) {
                this.mapHighlightEntityService.resetHighlight();
                this.mapHighlightEntityService.manageHighlightFeature(source, [entityId], entityType);
            }

            this.homeService.entityTableExtPageOpen.set(true);
        });
    }

    private async handleEntitySelection(feature: MapGeoJSONFeatureWithLayer): Promise<void> {
        if (this.isFieldInterventionPanelActive()) {
            return;
        }

        if (this.panelService.currentPanel() !== ActionsEnum.SYNOPTIC) {
            await this.navigateToEntityDetail(feature);

            return;
        }

        this.openNewWindowModal(feature);
    }

    private handleHighlightEntityBroadcastChannelEvent(
        event: MessageEvent<BroadcastChannelEvent<HighlightEntityBroadcastChannelData>>,
    ): void {
        const data = event.data;
        if (!data.additionalData) {
            return;
        }

        const { entityId, entityType, source } = data.additionalData;
        if (data?.message === BroadcastChannelMessageEnum.RESET_HIGHLIGHT_ENTITY || !entityType || !source) {
            this.homeService.entityTableExtPageOpen.set(false);
            this.mapHighlightEntityService.resetHighlight(source, entityType);

            return;
        }

        if (data?.message !== BroadcastChannelMessageEnum.HIGHLIGHT_ENTITY) {
            return;
        }

        if (!this.homeService.entityTableExtPageOpen()) {
            this.homeService.entityTableExtPageOpen.set(true);
        }
        this.mapHighlightEntityService.manageHighlightFeature(source, [entityId], entityType);
    }

    private handleImmersiveViewMarker(): void {
        if (!this.immersiveViewService.isImmersiveViewActive()) {
            this.removeImmersiveViewMarker();

            return;
        }

        const markerPosition: ImmersiveViewPosition | null = this.immersiveViewService.markerPosition();

        if (!markerPosition || !this.map) {
            return;
        }

        const coordinates: [number, number] = [markerPosition.lng, markerPosition.lat];

        if (this.immersiveViewMarker) {
            this.immersiveViewMarker.setLngLat(coordinates);

            return;
        }

        const div = document.createElement('div');

        div.innerHTML = IMMERSIVE_VIEW_MARKER_HTML;

        this.immersiveViewMarker = new maplibregl.Marker({
            anchor: 'bottom',
            element: div,
        })
            .setLngLat(coordinates)
            .addTo(this.map);

        setTimeout(() => {
            if (!this.map || !this.immersiveViewService.isImmersiveViewActive()) {
                return;
            }

            this.map.resize();

            this.map.flyTo({
                center: coordinates,
                duration: 1000,
            });
        }, 100);
    }

    private async handleLayerEditMode(
        mapClickEvent: MapMouseEvent,
        vertexSource?: MapGeoJSONFeature[],
        isAddVertex?: boolean,
    ): Promise<void> {
        await this.mapEditGeomService.manageEditEntity(mapClickEvent, vertexSource, isAddVertex);

        if (this.mapEditModeService.isEditingGeometryActive()) {
            return;
        }

        await this.redrawEditedSources();
    }

    private async handleMapClick(mapClickEvent: MapMouseEvent): Promise<void> {
        const pinLngLat = this.pinService.handleMapClick(mapClickEvent);

        if (pinLngLat) {
            this.openCreatePinModal(pinLngLat);

            return;
        }

        if (this.mapModeService.isBboxShapingActive) {
            return;
        }

        if (this.isMapClickDisabled()) {
            if (this.immersiveViewService.isImmersiveViewActive()) {
                this.onImmersiveViewMapClick(mapClickEvent);
            }

            return;
        }

        this.removeAddressMarker();

        if (this.immersiveViewService.isImmersiveViewActive()) {
            this.onImmersiveViewMapClick(mapClickEvent);

            return;
        }

        this.removeImmersiveViewMarker();

        if (this.mapEditModeService.isAddEntityModeActive() && !this.selectionService.isSelectionActive()) {
            if (!this.mapEditModeService.activeGeometryType()) {
                return;
            }

            await this.mapCreationGeomService.createEntity(mapClickEvent);

            return;
        }

        if (this.mapEditModeService.isAddLineVertexActive()) {
            await this.mapEditGeomService.manageSaveEditedEntity(mapClickEvent);

            await this.redrawEditedSources();

            this.mapEditGeomService.setGeomEditContextForAddLineVertexMode();

            return;
        }

        if (this.mapEditModeService.activeEditLayerIds().size && !this.selectionService.isSelectionActive()) {
            if (this.mapEditModeService.isEditVertexActive() || this.mapEditModeService.isAddVertexActive()) {
                this.handleLayerEditMode(mapClickEvent);
                this.mapEditModeService.isEditVertexActive.set(false);
                this.mapEditModeService.isAddVertexActive.set(false);
            }

            return;
        }

        const features: MapGeoJSONFeatureWithLayer[] = this.getFeatureInCoordinates(mapClickEvent.point);

        if (!features.length) {
            return;
        }
        if (this.selectionService.isEntitySelectionEnabled()) {
            await this.selectionService.manageSelectionFromClick(features, mapClickEvent);

            return;
        }

        if (features.length === 1) {
            await this.handleEntitySelection(features[0]);

            return;
        }

        if (this.isFieldInterventionPanelActive()) {
            return;
        }

        this.mapPopupService.createLayerMarkersPopup(features, mapClickEvent.lngLat, this.selectEntities(features));
    }

    private async handleMapSearch(): Promise<void> {
        this.isSearchedFeatureActive.set(true);

        const selectedItem = this.mapSearchService.selectedSearchItem();

        if (!this.map || !selectedItem) {
            return;
        }

        const selectedEntity: SelectedEntity = {
            ...(await this.depositService.getDepositEntityData(selectedItem)),
            identifier: selectedItem.name,
            layerId: selectedItem.data.parentId,
        };

        const currentSource = this.sourcesServices.sources().find((source: MapBaseSource<unknown>) => {
            try {
                AssertService.hasKey(source.data, 'id');

                return source.data['id'] === selectedEntity.layerId;
            } catch {
                return false;
            }
        });

        if (!selectedEntity.geom?.bbox || !currentSource) {
            return;
        }

        this.map.fitBounds(selectedEntity.geom.bbox as LngLatBoundsLike, { maxZoom: 18 });

        if (
            !this.map ||
            this.panelService.currentPanel() === ActionsEnum.STATISTICS ||
            this.panelService.currentPanel() === ActionsEnum.SYNOPTIC ||
            this.dataExportService.isActive()
        ) {
            return;
        }

        this.map.on('idle', () => {
            if (!this.isSearchedFeatureActive()) {
                return;
            }

            this.getSearchedFeature(selectedEntity, currentSource.sourceId);
        });
    }

    private async handleMoveEndEvent(event: Event & { 0?: ResizeObserverEntry; fitBounds?: boolean }): Promise<void> {
        this.selectionService.refreshSelectionHighlightsForVisibleLayers();

        if (!this.map || event['0'] instanceof ResizeObserverEntry || this.mapModeService.isBboxShapingActive) {
            return;
        }

        if (this.map) {
            this.mapGeohashService.updateCentralGeohash(this.map);
        }

        const excludedSources = this.sources()?.filter((source: MapBaseSource<unknown>) => {
            const isLayer: boolean = source instanceof MapLayerSource;
            const isFieldIntervention: boolean = source instanceof MapFieldInterventionSource;

            const isInZoomRange: boolean =
                isLayer && this.mapLayerService.isZoomIncluded(source.maxzoom, source.minzoom);
            const layerUsedInEditMode: boolean =
                this.mapEditModeService.activeEditLayerIds().has(source.sourceId) &&
                this.mapEditModeService.isMapEditModeActive();
            const isCustomSourceIndicator = source.sourceId === this.mapClickIndicatorService.customIndicatorSourceId;

            return (
                isInZoomRange ||
                (source instanceof MapLayerSource &&
                    [Layer.TypeEnum.GEOM, Layer.TypeEnum.WMS, Layer.TypeEnum.WFS].includes(source.data.type)) ||
                isCustomSourceIndicator ||
                isFieldIntervention ||
                source instanceof MapGeomSource ||
                layerUsedInEditMode
            );
        });

        const tableSources = this.sources().filter((source: MapBaseSource<unknown>) => {
            return (
                (source instanceof MapLayerSource &&
                    [Layer.TypeEnum.TABLE, Layer.TypeEnum.INTERSTANDARD, Layer.TypeEnum.REFERENCE_DATA].includes(
                        source.data.type,
                    )) ||
                (source instanceof MapFieldInterventionSource &&
                    !excludedSources.some((excludedSource) => excludedSource.sourceId === source.sourceId))
            );
        });

        this.updateSourceActivation(tableSources);

        if (!this.currentUrl()) {
            return;
        }

        const isCreateFieldInterventionPage =
            this.currentUrl()!.includes(RoutePathEnum.FIELD_INTERVENTIONS) &&
            this.currentUrl()!.includes(RoutePathEnum.FIELD_INTERVENTION_CREATE);

        const isChangeSelectionFieldInterventionPage =
            this.currentUrl()!.includes(RoutePathEnum.FIELD_INTERVENTIONS) &&
            this.currentUrl()!.includes(RoutePathEnum.FIELD_INTERVENTION_CHANGE_SELECTION);

        if (!isCreateFieldInterventionPage && !isChangeSelectionFieldInterventionPage) {
            this.mapSourceService.clearSources(MapComponent.backgroundSource(), excludedSources);
        }

        tableSources.forEach((source: MapBaseSource<unknown>) => {
            if (source instanceof MapLayerSource) {
                source.isActivated = this.mapLayerService.isZoomIncluded(source.maxzoom, source.minzoom);
            }
        });

        await this.drawSources(tableSources, true);

        await this.applyFilters();

        this.networkPathMapService.moveNetworkPathToTop();

        MapSourceService.checkSourcesZoom.set({
            sourcesLegend: true,
            sourcesPanel: true,
        });
    }

    private async handleVertexClick(
        mapClickEvent: MapMouseEvent,
        layerId: string,
        isAddVertex: boolean,
    ): Promise<boolean> {
        if (!this.mapEditModeService.activeEditLayerIds().size) {
            return false;
        }

        let clickedVertexFeatures = this.map?.queryRenderedFeatures(mapClickEvent.point, {
            layers: [layerId],
        });

        const clickedVertexFeature = clickedVertexFeatures?.[0];

        if (!clickedVertexFeatures?.length || !clickedVertexFeature) {
            return false;
        }

        if (isAddVertex) {
            if (clickedVertexFeature.properties?.['isLineType']) {
                await this.mapEditGeomService.handleAddLineVertex(clickedVertexFeature);

                return true;
            }

            await this.handleLayerEditMode(mapClickEvent, [clickedVertexFeature], isAddVertex);

            return true;
        }

        const uniqueEntityIds = new Set(
            clickedVertexFeatures.map((editVertexFeature) => editVertexFeature.properties['entityId']),
        );

        if (uniqueEntityIds.size > 1) {
            const editVertexFeatureMap = new Map(
                clickedVertexFeatures.map((editVertexFeature) => [
                    editVertexFeature.properties['entityId'],
                    editVertexFeature,
                ]),
            );

            const selectedEntityIds = await this.mapPopupService.openSelectionPopup(
                [...editVertexFeatureMap.values()],
                mapClickEvent.lngLat,
            );

            const selectedVertexFeatures = clickedVertexFeatures.filter((editVertexFeature) =>
                selectedEntityIds.includes(editVertexFeature.properties['entityId']),
            );

            if (!selectedVertexFeatures.length) {
                return false;
            }

            clickedVertexFeatures = selectedVertexFeatures;
        }

        await this.handleLayerEditMode(mapClickEvent, clickedVertexFeatures);

        return true;
    }

    private initializeMapEvents(): void {
        if (!this.map || !this.projectBloc.value()) {
            return;
        }

        this.map.on('resize', this.mapInteractionService.onResize);

        this.map.once('load', async () => {
            MapBaseService.isMapReady.set(true);
            if (this.map) {
                this.map.jumpTo({
                    center: this.center() ?? MapComponent.defaultCenter,
                    zoom: MapBaseService.defaultZoom,
                });
            }

            await this.drawSources(this.sources()!, true);

            this.applyFilters();

            this.map?.once('idle', () => {
                this.selectionService.refreshSelectionHighlightsForVisibleLayers();
                if (!this.selectionService.isSelectionActive()) {
                    return;
                }
                this.zoneDelimiterService.restoreZoneDelimited();
            });
        });

        this.map.on('moveend', this.handleMoveEndEvent.bind(this));
        this.map.on('zoomend', () => this.mapInteractionService.onZoomEnd());

        if (!this.canClickFeature()) {
            return;
        }

        this.map.on('contextmenu', async () => {
            if (
                this.mapEditModeService.activeGeometryType() === ActiveGeometryTypeEnum.SYMBOL ||
                this.selectionService.isSelectionActive()
            ) {
                return;
            }

            if (this.measurementToolService.isActive()) {
                await this.measurementToolService.finishMeasurement();

                return;
            }

            if (this.mapEditModeService.isEditVertexActive() || this.mapEditModeService.isAddVertexActive()) {
                this.mapEditGeomService.cancelSelectedVertex();
                await this.mapSourceService.applyEditStyle(
                    [...this.mapEditModeService.activeEditLayerIds()],
                    this.mapEditModeService.editEntityId(),
                );

                return;
            }

            if (this.mapEditModeService.isAddEntityModeActive()) {
                await this.mapCreationGeomService.createShape();

                return;
            }

            if (!this.mapEditModeService.isAddLineVertexActive()) {
                return;
            }

            this.mapEditGeomService.resetAddLineVertex();
            await this.mapSourceService.applyEditStyle(
                [...this.mapEditModeService.activeEditLayerIds()],
                this.mapEditModeService.editEntityId(),
            );
        });

        this.map.on('click', MapEditModeService.EDIT_LAYER, async (mapClickEvent) => {
            if (this.isEditActionActive() || this.mapEditModeService.isEditVertexActive()) {
                return;
            }
            this.isEditActionActive.set(true);

            const isEditVertexEnable = await this.handleVertexClick(
                mapClickEvent,
                MapEditModeService.EDIT_LAYER,
                false,
            );
            this.mapEditModeService.isEditVertexActive.set(isEditVertexEnable);
            this.isEditActionActive.set(false);
        });

        this.map.on('click', MapEditModeService.EDIT_ADD_VERTEX_LAYER, async (mapClickEvent) => {
            if (this.isEditActionActive()) {
                return;
            }
            this.isEditActionActive.set(true);

            const isAddVertexEnable = await this.handleVertexClick(
                mapClickEvent,
                MapEditModeService.EDIT_ADD_VERTEX_LAYER,
                true,
            );
            this.mapEditModeService.isAddVertexActive.set(isAddVertexEnable);
            this.isEditActionActive.set(false);
        });

        this.map.on('click', async (mapClickEvent: MapMouseEvent): Promise<void> => {
            await this.handleMapClick(mapClickEvent);
        });

        this.map.on('styledata', this.moveEditLayerOnTop.bind(this));

        this.mapClickIndicatorService.registerZoomListener();
    }

    private moveEditLayerOnTop(): void {
        const layers = this.map?.getStyle().layers;
        if (!layers) {
            return;
        }

        const lastLayer = layers[layers.length - 1];

        if ([MapEditModeService.EDIT_LAYER, MapEditModeService.EDIT_ADD_VERTEX_LAYER].includes(lastLayer?.id)) {
            return;
        }

        if (this.map?.getLayer(MapEditModeService.EDIT_LAYER)) {
            this.map.moveLayer(MapEditModeService.EDIT_LAYER);
        }

        if (!this.map?.getLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER)) {
            return;
        }

        this.map?.moveLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER);
    }

    private async navigateToEntityDetail(feature: MapGeoJSONFeatureWithLayer): Promise<void> {
        const navigationService = this.injector.get(NavigationStateService);
        navigationService.markNavigated();

        const source = this.sources().find((source: MapBaseSource<unknown>): boolean => {
            return source.sourceId === feature?.source;
        });

        if (!source) {
            return;
        }

        if (source instanceof MapFieldInterventionSource) {
            await this.navigateToFieldInterventionEntity(feature);
            this.mapHighlightEntityService.highlightMapEntity(feature, source);

            return;
        }

        this.featureService.setData(feature);

        await this.router.navigate(
            [RoutePathEnum.LAYERS, feature.layer.source, RouteNameEnum.ENTITY_DETAIL, feature.properties['uuid']],
            {
                relativeTo: this.activatedRoute,
            },
        );

        this.mapHighlightEntityService.highlightMapEntity(feature, source);
    }

    private async navigateToFieldInterventionEntity(feature: MapGeoJSONFeatureWithLayer): Promise<void> {
        const fieldInterventionEntity = await firstValueFrom(
            this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGetOne({
                id: feature.properties['entityId'],
            }),
        );

        feature.source = fieldInterventionEntity.layer;
        this.featureService.setData(feature, fieldInterventionEntity.id);

        await this.router.navigate(
            [
                RoutePathEnum.LAYERS,
                fieldInterventionEntity.layer,
                RouteNameEnum.ENTITY_DETAIL,
                feature.properties['entityId'],
            ],
            {
                relativeTo: this.activatedRoute,
            },
        );
    }

    private onImmersiveViewMapClick(mapClickEvent: MapMouseEvent): void {
        const { lat, lng } = mapClickEvent.lngLat;

        this.immersiveViewService.openAtLocation({ lat, lng });
    }

    private openBasemapModal(): void {
        if (!this.map) {
            return;
        }

        if (this.baseMapDialogRef && this.baseMapDialogRef.getState() !== MatDialogState.CLOSED) {
            this.closeBasemapModal();

            return;
        }

        this.pinService.close();

        const dialogRef = this.matDialog.open(BasemapManagementModalComponent, {
            data: { map: this.map },
            hasBackdrop: false,
            panelClass: ['basemap-dialog', 'basemap-dialog--compact'],
            position: {
                bottom: '2.8rem',
                right: '4.1rem',
            },
        });

        this.baseMapDialogRef = dialogRef;
        dialogRef.afterClosed().subscribe(() => {
            if (this.baseMapDialogRef === dialogRef) {
                this.basemapService.isActive.set(false);
                this.baseMapDialogRef = null;
            }
        });
    }

    private openCreatePinModal(coordinates: [number, number]): void {
        if (this.pinService.isCreateModalOpen()) {
            return;
        }

        this.pinService.isCreateModalOpen.set(true);

        const dialogRef = this.matDialog.open<ManagePinModalComponent, ManagePinModalData, void>(
            ManagePinModalComponent,
            {
                autoFocus: false,
                data: { coordinates },
                hasBackdrop: false,
                panelClass: 'pin-create-dialog',
                width: '48rem',
            },
        );

        dialogRef.afterClosed().subscribe(() => {
            this.pinService.isCreateModalOpen.set(false);
        });
    }

    private openMapExportModal(): void {
        if (!this.map) {
            return;
        }

        this.matDialog.open(MapExportModalComponent, { data: { map: this.map }, width: '40rem' });
    }

    private openNewWindowModal(feature: MapGeoJSONFeatureWithLayer): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmIcon: ['far', 'rectangle-history-circle-plus'],
                confirmLabel: this.translocoService.translate('newWindow'),
                description: this.translocoService.translate('thisWillOpenNewWindow'),
                title: this.translocoService.translate('enableMapInteractions'),
                type: ModalType.WARNING,
            },
            width: '20vw',
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }
            this.broadcastChannelService.handleOpenInNewWindow(RoutePathEnum.EXT);

            await this.navigateToEntityDetail(feature);
        });
    }

    private async redrawAfterExitEditMode(): Promise<void> {
        try {
            await this.drawSources(this.sources(), false, true);
        } catch {
            throw new Error('[Map] redrawAfterExitEditMode skipped:');
        }
    }

    private async redrawEditedSources(): Promise<void> {
        const editedSource = this.sources().filter((source: MapBaseSource<unknown>) =>
            this.mapEditModeService.activeEditLayerIds().has(source.sourceId),
        );

        if (!editedSource.length) {
            return;
        }

        const copiedSources = structuredClone(editedSource);
        copiedSources.forEach((copiedSource) => {
            copiedSource.minzoom = null;
            copiedSource.maxzoom = null;
        });

        await this.drawSources(copiedSources, false);
    }

    private removeAddressMarker(): void {
        this.addressMarker?.remove();
        this.addressMarker = null;
    }

    private removeImmersiveViewMarker(): void {
        if (!this.immersiveViewMarker) {
            return;
        }

        this.immersiveViewMarker.remove();
        this.immersiveViewMarker = null;
    }

    private removeRenderedEntityFeatureListener(listener: () => void): void {
        this.map?.off('render', listener);
    }

    private resolveAvailableClickSources(): MapBaseSource<unknown>[] {
        if (this.createFieldInterventionService.isCreateFieldIntervention()) {
            return this.createFieldInterventionService.currentSources();
        }

        if (this.entitiesTableByLayerService.isPlanningSelectionActive()) {
            return this.entitiesTableByLayerService.currentSources();
        }

        return this.sources();
    }

    private selectEntities(features: MapGeoJSONFeatureWithLayer[]) {
        return (markerClickEvent: Event): void => {
            const feature = this.mapPopupService.resolveClickedFeature(features, markerClickEvent);

            if (!feature) {
                return;
            }

            this.handleEntitySelection(feature);
        };
    }

    private toggleEditMode(): void {
        if (!this.editModeCapabilities().enabled) {
            return;
        }

        this.editEntityDetailService.isEditMode.set(!this.editEntityDetailService.isEditMode());
    }

    private updateSourceActivation(sources: MapBaseSource<unknown>[]): void {
        const areSourcesActivated = sources
            .filter(
                (source: MapBaseSource<unknown>) =>
                    source instanceof MapLayerSource && [Layer.TypeEnum.TABLE].includes(source.data.type),
            )
            .some((source: MapBaseSource<unknown>) => {
                const isZoomIncluded = this.mapLayerService.isZoomIncluded(source.maxzoom, source.minzoom);
                const isSourceActivated = !!this.map?.getSource(source.sourceId);

                return (isZoomIncluded && !isSourceActivated) || (!isZoomIncluded && isSourceActivated);
            });
        MapSourceService.sourcesActivationChanged.set(areSourcesActivated);

        setTimeout(() => {
            if (MapSourceService.sourcesActivationChanged()) {
                MapSourceService.sourcesActivationChanged.set(false);
            }
        }, 5000);
    }

    // Wait until the map is ready/stable before zooming in.
    private waitForMapIdleOnce(): Promise<void> {
        return new Promise((resolve) => {
            if (!this.map) {
                resolve();

                return;
            }

            this.map.once('idle', resolve);
            this.map.redraw();
        });
    }

    // Wait until the entity has actually been rendered before enabling geometric editing.
    private waitForRenderedEntityFeature(
        entityId: string,
        timeoutInMilliseconds = 5000,
    ): Promise<MapGeoJSONFeature | undefined> {
        const startedAt = Date.now();

        return new Promise((resolve) => {
            const listener = this.createRenderedEntityFeatureListener(
                entityId,
                startedAt,
                timeoutInMilliseconds,
                resolve,
            );

            this.map?.on('render', listener);
            listener();
        });
    }
}
